'use client';

import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import type { ElasticRetailer } from '@//app/api/retailers/[slug]';
import { Alert } from '@//components/Alert';
import { Grid } from '@//components/Grid';
import { Section } from '@//components/Section';
import ProductCardContainer from '@//containers/ProductCardContainer';
import ProductCardSkeleton from '@//containers/ProductCardSkeleton';
import useVehicles from '@//data/search/vehicles/useVehicles';
import { useMarketMetadata } from '@//providers/MarketSettingsProvider';
import { extractQueryForVehicleSearch } from '@//utils/vehicles-search/utils';
import RetailerVehiclesPagination from './Pagination';

interface RetailerItemsSectionProps {
  retailer: ElasticRetailer;
}

const RetailerItemsSection = ({ retailer }: RetailerItemsSectionProps) => {
  const searchParams = useSearchParams();
  const marketMetadata = useMarketMetadata();
  const t = useTranslations('pages.retailer');

  const params = useMemo(() => {
    const newParams = extractQueryForVehicleSearch(searchParams);
    newParams.set('branches', retailer.name);
    return newParams;
  }, [searchParams.toString(), retailer.name]);

  // Fetch vehicles using the hook
  const { data: vehiclesResponse, isLoading, isSuccess } = useVehicles(params);

  const vehicles = vehiclesResponse?.documentList.documents || [];
  const totalHits = vehiclesResponse?.stats.totalHits || 0;

  const skeletonArray = new Array(6).fill(null);

  useEffect(() => {
    if (searchParams.has('offset') && vehicles.length === 0 && isSuccess) {
      window.history.replaceState(
        null,
        '',
        `${marketMetadata.pageLinks.retailersPage}/${retailer.slug}`,
      );
    }
  }, [searchParams, vehicles.length, isSuccess]);

  return (
    <Section id="vehicles" heading={t('vehicles.heading', { retailerName: retailer.name })}>
      {isLoading ? (
        <Grid columns={3}>
          {skeletonArray.map((_, index) => (
            <ProductCardSkeleton key={index} />
          ))}
        </Grid>
      ) : totalHits > 0 ? (
        <>
          <Grid
            columns={3}
            role="region"
            aria-label={t('vehicles.ariaLabel')}
            id="retailer-vehicles"
          >
            {vehicles.map((item, index) => (
              <ProductCardContainer
                key={item._id}
                index={index}
                parameters={vehiclesResponse?.query.parameters || ''}
                testId={`retailer-product-card-${index + 1}`}
                item={item}
                itemPage={marketMetadata.pageLinks.itemPage}
                currency={marketMetadata.currency}
              />
            ))}
          </Grid>
          <RetailerVehiclesPagination
            rootUrl={`${marketMetadata.pageLinks.retailersPage}/${retailer.slug}`}
            pagination={vehiclesResponse?.documentList.pagination}
          />
        </>
      ) : (
        <Alert id="alert-no-vehicles" heading={t('vehicles.noResults.heading')}>
          {t('vehicles.noResults.message')}
        </Alert>
      )}
    </Section>
  );
};

export default RetailerItemsSection;
